import { useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  GridFour,
  SunHorizon,
  FrameCorners,
  SlidersHorizontal,
  Eye,
  Palette,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

/* ─── bilingual content objects ─── */
export const content = {
  en: {
    fundamentals_heading: "Photography Fundamentals",
    fundamentals_sub: "Master the core principles every photographer needs to know",

    rot_title: "Rule of Thirds",
    rot_desc: "Place key subjects along imaginary grid lines or at their intersections for a naturally balanced, dynamic composition.",
    rot_tip1: "Position eyes at the upper-third intersections in portraits.",
    rot_tip2: "Place the horizon on the top or bottom third line — never dead-center.",
    rot_tip3: "Moving subjects should have space in front of them (\"lead room\").",
    rot_tip4: "Enable the grid overlay in your camera's viewfinder for practice.",
    rot_interactive_label: "Interactive Grid",
    rot_click_hint: "Click a grid intersection to see a tip",
    rot_points: [
      "Strong position for a person's face or eye",
      "Great for a lighthouse, tree, or focal subject",
      "Place the horizon here in landscape shots",
      "Perfect for foreground interest like flowers",
    ],

    lc_title: "Lighting & Color Temperature",
    lc_desc: "Light color is measured in Kelvin (K). Understanding color temperature helps you white-balance correctly and use light creatively.",
    lc_items: [
      { k: "1,800K", label: "Candle / Match", color: "#FF8C00" },
      { k: "2,700K", label: "Warm Tungsten Bulb", color: "#FFA830" },
      { k: "3,500K", label: "Halogen / Warm LED", color: "#FFBE50" },
      { k: "4,300K", label: "Fluorescent", color: "#FFD88E" },
      { k: "5,200K", label: "Noon Daylight", color: "#FFF4D6" },
      { k: "6,000K", label: "Overcast Sky", color: "#E8F0FF" },
      { k: "7,500K", label: "Deep Shade", color: "#C8D8FF" },
      { k: "10,000K", label: "Blue Sky / Twilight", color: "#99BBFF" },
    ],
    lc_golden: "Golden Hour (first/last hour of sunlight) produces the warmest, most flattering natural light — around 3,500K.",
    lc_blue: "Blue Hour (just before sunrise / after sunset) creates cool, moody tones — roughly 8,000–10,000K.",

    comp_title: "Composition Techniques",
    comp_desc: "Composition is how you arrange elements in the frame. Strong composition turns snapshots into photographs.",
    comp_cards: [
      { name: "Leading Lines", tip: "Use roads, fences, or rivers to guide the viewer's eye toward the subject." },
      { name: "Framing", tip: "Shoot through doorways, arches, or branches to create a natural border." },
      { name: "Symmetry & Patterns", tip: "Find repeating shapes or reflections — then break the pattern for impact." },
      { name: "Fill the Frame", tip: "Get closer. Eliminate distracting backgrounds by letting the subject dominate." },
      { name: "Negative Space", tip: "Leave empty space around the subject to evoke simplicity and focus." },
      { name: "Foreground Interest", tip: "Place an object in the near-ground to add depth to landscapes." },
    ],

    tech_title: "Technical Mastery",
    tech_desc: "Knowing your camera's modes and settings lets you react quickly in any situation.",
    tech_focus_title: "Focus Modes",
    tech_focus: [
      { mode: "AF-S / One-Shot", use: "Still subjects — portraits, products, landscapes. Camera locks focus once." },
      { mode: "AF-C / AI Servo", use: "Moving subjects — sports, wildlife, kids. Camera tracks focus continuously." },
      { mode: "Manual Focus (MF)", use: "Macro, astrophotography, or when autofocus hunts in low light." },
    ],
    tech_meter_title: "Metering Modes",
    tech_meter: [
      { mode: "Matrix / Evaluative", use: "General shooting. Averages the whole scene — good default." },
      { mode: "Center-Weighted", use: "Portraits. Prioritises the center of the frame." },
      { mode: "Spot", use: "High-contrast scenes. Meters a tiny area — use on the subject." },
    ],
    tech_wb_title: "White Balance Presets",
    tech_wb: [
      { preset: "Auto (AWB)", when: "Mixed or changing light — let the camera decide." },
      { preset: "Daylight", when: "Sunny outdoor conditions." },
      { preset: "Cloudy", when: "Overcast skies — adds slight warmth." },
      { preset: "Shade", when: "Open shade — adds more warmth to compensate blue tones." },
      { preset: "Tungsten", when: "Indoor incandescent bulbs — cools the orange cast." },
      { preset: "Fluorescent", when: "Office / gym lighting — corrects green tint." },
    ],

    pp_title: "Post-Processing Basics",
    pp_desc: "Editing is part of the craft. These fundamentals apply in any software (Lightroom, Snapseed, GIMP).",
    pp_steps: [
      { step: "Crop & Straighten", detail: "Fix tilted horizons and tighten composition before anything else." },
      { step: "Exposure & Brightness", detail: "Adjust overall brightness. Use the histogram — avoid clipping highlights or shadows." },
      { step: "White Balance", detail: "Correct color casts. Slide towards blue if too warm, towards yellow if too cool." },
      { step: "Contrast & Clarity", detail: "Contrast separates lights from darks. Clarity adds mid-tone punch — use sparingly." },
      { step: "Saturation & Vibrance", detail: "Vibrance boosts muted colors gently. Saturation affects everything — easy to overdo." },
      { step: "Sharpening & Export", detail: "Apply sharpening last. Export at the correct resolution for print vs. web." },
    ],
  },

  es: {
    fundamentals_heading: "Fundamentos de Fotografía",
    fundamentals_sub: "Domina los principios básicos que todo fotógrafo necesita conocer",

    rot_title: "Regla de los Tercios",
    rot_desc: "Coloca los sujetos clave a lo largo de líneas imaginarias de cuadrícula o en sus intersecciones para una composición equilibrada y dinámica.",
    rot_tip1: "Posiciona los ojos en las intersecciones del tercio superior en retratos.",
    rot_tip2: "Coloca el horizonte en la línea del tercio superior o inferior — nunca en el centro exacto.",
    rot_tip3: "Los sujetos en movimiento deben tener espacio delante (\"espacio de avance\").",
    rot_tip4: "Activa la cuadrícula en el visor de tu cámara para practicar.",
    rot_interactive_label: "Cuadrícula Interactiva",
    rot_click_hint: "Haz clic en una intersección para ver un consejo",
    rot_points: [
      "Posición fuerte para el rostro o los ojos de una persona",
      "Excelente para un faro, árbol o sujeto focal",
      "Coloca el horizonte aquí en fotos de paisaje",
      "Perfecto para interés en primer plano como flores",
    ],

    lc_title: "Iluminación y Temperatura de Color",
    lc_desc: "El color de la luz se mide en Kelvin (K). Entender la temperatura de color te ayuda a equilibrar el blanco correctamente y usar la luz creativamente.",
    lc_items: [
      { k: "1,800K", label: "Vela / Cerillo", color: "#FF8C00" },
      { k: "2,700K", label: "Bombilla Tungsteno", color: "#FFA830" },
      { k: "3,500K", label: "Halógeno / LED cálido", color: "#FFBE50" },
      { k: "4,300K", label: "Fluorescente", color: "#FFD88E" },
      { k: "5,200K", label: "Luz de Mediodía", color: "#FFF4D6" },
      { k: "6,000K", label: "Cielo Nublado", color: "#E8F0FF" },
      { k: "7,500K", label: "Sombra Profunda", color: "#C8D8FF" },
      { k: "10,000K", label: "Cielo Azul / Crepúsculo", color: "#99BBFF" },
    ],
    lc_golden: "Hora Dorada (primera/última hora de sol) produce la luz natural más cálida y favorecedora — alrededor de 3,500K.",
    lc_blue: "Hora Azul (justo antes del amanecer / después del atardecer) crea tonos fríos y dramaticos — aproximadamente 8,000–10,000K.",

    comp_title: "Técnicas de Composición",
    comp_desc: "La composición es como organizas los elementos en el encuadre. Una buena composición transforma instantáneas en fotografías.",
    comp_cards: [
      { name: "Líneas Guía", tip: "Usa caminos, cercas o ríos para dirigir la mirada del espectador hacia el sujeto." },
      { name: "Enmarcado", tip: "Dispara a través de puertas, arcos o ramas para crear un borde natural." },
      { name: "Simetría y Patrones", tip: "Encuentra formas repetitivas o reflejos — luego rompe el patrón para causar impacto." },
      { name: "Llenar el Encuadre", tip: "Acercate. Elimina fondos distractores dejando que el sujeto domine." },
      { name: "Espacio Negativo", tip: "Deja espacio vacio alrededor del sujeto para evocar simplicidad y enfoque." },
      { name: "Interés en Primer Plano", tip: "Coloca un objeto cercano para añadir profundidad a los paisajes." },
    ],

    tech_title: "Dominio Técnico",
    tech_desc: "Conocer los modos y ajustes de tu cámara te permite reaccionar rápido en cualquier situación.",
    tech_focus_title: "Modos de Enfoque",
    tech_focus: [
      { mode: "AF-S / One-Shot", use: "Sujetos quietos — retratos, productos, paisajes. La cámara bloquea el enfoque una vez." },
      { mode: "AF-C / AI Servo", use: "Sujetos en movimiento — deportes, fauna, niños. La cámara rastrea el enfoque continuamente." },
      { mode: "Enfoque Manual (MF)", use: "Macro, astrofotografía, o cuando el autoenfoque falla en poca luz." },
    ],
    tech_meter_title: "Modos de Medición",
    tech_meter: [
      { mode: "Matricial / Evaluativa", use: "Uso general. Promedia toda la escena — buen valor predeterminado." },
      { mode: "Ponderada al Centro", use: "Retratos. Prioriza el centro del encuadre." },
      { mode: "Puntual", use: "Escenas de alto contraste. Mide un area mínima — úsalo sobre el sujeto." },
    ],
    tech_wb_title: "Preajustes de Balance de Blancos",
    tech_wb: [
      { preset: "Auto (AWB)", when: "Luz mixta o cambiante — deja que la cámara decida." },
      { preset: "Luz de Día", when: "Condiciones soleadas al aire libre." },
      { preset: "Nublado", when: "Cielos nublados — añade ligera calidez." },
      { preset: "Sombra", when: "Sombra abierta — añade más calidez para compensar tonos azules." },
      { preset: "Tungsteno", when: "Bombillas incandescentes interiores — enfria el tono anaranjado." },
      { preset: "Fluorescente", when: "Iluminación de oficina / gimnasio — corrige el tinte verde." },
    ],

    pp_title: "Fundamentos de Post-Procesado",
    pp_desc: "Editar es parte del oficio. Estos fundamentos aplican en cualquier software (Lightroom, Snapseed, GIMP).",
    pp_steps: [
      { step: "Recortar y Enderezar", detail: "Corrige horizontes inclinados y ajusta la composición antes que nada." },
      { step: "Exposición y Brillo", detail: "Ajusta el brillo general. Usa el histograma — evita recortar luces o sombras." },
      { step: "Balance de Blancos", detail: "Corrige dominantes de color. Desliza hacia azul si está muy cálido, hacia amarillo si está muy frío." },
      { step: "Contraste y Claridad", detail: "El contraste separa luces de oscuros. La claridad añade fuerza a los tonos medios — úsala con moderación." },
      { step: "Saturación y Vibrancia", detail: "La vibrancia realza colores apagados suavemente. La saturación afecta todo — es fácil exagerar." },
      { step: "Enfoque y Exportar", detail: "Aplica enfoque al final. Exporta a la resolución correcta para impresión vs. web." },
    ],
  },
};

/* ─── Rule of Thirds Interactive Grid ─── */
function RuleOfThirdsGrid({ c }) {
  const [activePoint, setActivePoint] = useState(null);
  const points = [
    { x: "33.3%", y: "33.3%", idx: 0 },
    { x: "66.6%", y: "33.3%", idx: 1 },
    { x: "33.3%", y: "66.6%", idx: 2 },
    { x: "66.6%", y: "66.6%", idx: 3 },
  ];

  return (
    <div className="space-y-3">
      <span className="text-[#F5A524] text-xs font-medium uppercase tracking-widest">
        {c.rot_interactive_label}
      </span>
      <div
        className="relative w-full aspect-[3/2] bg-[#0A0A0A] rounded-lg overflow-hidden border border-white/10"
        data-testid="rot-grid"
      >
        {/* Grid lines */}
        <div className="absolute inset-0">
          <div className="absolute left-1/3 top-0 bottom-0 w-px bg-[#F5A524]/30" />
          <div className="absolute left-2/3 top-0 bottom-0 w-px bg-[#F5A524]/30" />
          <div className="absolute top-1/3 left-0 right-0 h-px bg-[#F5A524]/30" />
          <div className="absolute top-2/3 left-0 right-0 h-px bg-[#F5A524]/30" />
        </div>
        {/* Intersection points */}
        {points.map((p) => (
          <button
            key={p.idx}
            onClick={() => setActivePoint(activePoint === p.idx ? null : p.idx)}
            className={`absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition-all cursor-pointer ${
              activePoint === p.idx
                ? "bg-[#F5A524] border-[#F5A524] scale-125"
                : "bg-transparent border-[#F5A524]/60 hover:border-[#F5A524] hover:scale-110"
            }`}
            style={{ left: p.x, top: p.y }}
            data-testid={`rot-point-${p.idx}`}
          />
        ))}
        {/* Tooltip */}
        {activePoint !== null && (
          <div className="absolute bottom-3 left-3 right-3 bg-black/80 backdrop-blur-sm rounded-lg p-3 text-sm text-white border border-[#F5A524]/30">
            {c.rot_points[activePoint]}
          </div>
        )}
      </div>
      <p className="text-[#71717A] text-xs">{c.rot_click_hint}</p>
    </div>
  );
}

export default function FundamentalsSection() {
  const { lang } = useTranslation();
  const c = content[lang] || content.en;

  return (
    <div className="space-y-8">
      {/* Section heading */}
      <motion.div variants={item}>
        <div className="flex items-center gap-2 mb-1">
          <div className="h-px flex-1 bg-gradient-to-r from-[#F5A524]/40 to-transparent" />
          <span className="text-[#F5A524] text-xs font-medium uppercase tracking-[0.2em]">
            {c.fundamentals_heading}
          </span>
          <div className="h-px flex-1 bg-gradient-to-l from-[#F5A524]/40 to-transparent" />
        </div>
        <p className="text-[#71717A] text-sm text-center">{c.fundamentals_sub}</p>
      </motion.div>

      {/* 1 ─ Rule of Thirds */}
      <motion.div
        variants={item}
        className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        data-testid="fundamentals-rot"
      >
        <div className="flex items-center gap-3 mb-4">
          <GridFour weight="fill" className="w-6 h-6 text-[#F5A524]" />
          <div>
            <h3 className="font-['Outfit'] text-lg text-white font-medium">{c.rot_title}</h3>
            <p className="text-[#71717A] text-sm">{c.rot_desc}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RuleOfThirdsGrid c={c} />
          <div className="space-y-3">
            {[c.rot_tip1, c.rot_tip2, c.rot_tip3, c.rot_tip4].map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F5A524] shrink-0" />
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* 2 ─ Lighting & Color Temperature */}
      <motion.div
        variants={item}
        className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        data-testid="fundamentals-lighting"
      >
        <div className="flex items-center gap-3 mb-4">
          <SunHorizon weight="fill" className="w-6 h-6 text-[#F5A524]" />
          <div>
            <h3 className="font-['Outfit'] text-lg text-white font-medium">{c.lc_title}</h3>
            <p className="text-[#71717A] text-sm">{c.lc_desc}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex gap-1 sm:gap-2">
            {c.lc_items.map((it, i) => (
              <div key={i} className="flex-1 text-center group" data-testid={`kelvin-${i}`}>
                <div
                  className="h-8 rounded-md mb-1.5 transition-transform group-hover:scale-y-125"
                  style={{ backgroundColor: it.color }}
                />
                <div className="text-[10px] sm:text-xs text-white font-['JetBrains_Mono']">{it.k}</div>
                <div className="text-[9px] sm:text-[10px] text-[#71717A] leading-tight hidden sm:block">{it.label}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
              <span className="text-[#F5A524] text-xs font-semibold uppercase">Golden Hour</span>
              <p className="text-[#A1A1AA] text-sm mt-1">{c.lc_golden}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
              <span className="text-[#6EA8FE] text-xs font-semibold uppercase">Blue Hour</span>
              <p className="text-[#A1A1AA] text-sm mt-1">{c.lc_blue}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 3 ─ Composition Techniques */}
      <motion.div
        variants={item}
        className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        data-testid="fundamentals-composition"
      >
        <div className="flex items-center gap-3 mb-4">
          <FrameCorners weight="fill" className="w-6 h-6 text-[#F5A524]" />
          <div>
            <h3 className="font-['Outfit'] text-lg text-white font-medium">{c.comp_title}</h3>
            <p className="text-[#71717A] text-sm">{c.comp_desc}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {c.comp_cards.map((card, i) => (
            <div
              key={i}
              className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 hover:border-[#F5A524]/30 transition-colors"
              data-testid={`comp-card-${i}`}
            >
              <div className="text-white font-medium text-sm mb-1">{card.name}</div>
              <p className="text-[#A1A1AA] text-xs leading-relaxed">{card.tip}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* 4 ─ Technical Mastery */}
      <motion.div
        variants={item}
        className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        data-testid="fundamentals-technical"
      >
        <div className="flex items-center gap-3 mb-4">
          <SlidersHorizontal weight="fill" className="w-6 h-6 text-[#F5A524]" />
          <div>
            <h3 className="font-['Outfit'] text-lg text-white font-medium">{c.tech_title}</h3>
            <p className="text-[#71717A] text-sm">{c.tech_desc}</p>
          </div>
        </div>
        <div className="space-y-5">
          {/* Focus Modes */}
          <div>
            <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-2">{c.tech_focus_title}</h4>
            <div className="space-y-2">
              {c.tech_focus.map((f, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-white font-['JetBrains_Mono'] text-sm shrink-0 w-36">{f.mode}</span>
                  <span className="text-[#A1A1AA] text-sm">{f.use}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Metering Modes */}
          <div>
            <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-2">{c.tech_meter_title}</h4>
            <div className="space-y-2">
              {c.tech_meter.map((m, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-white font-['JetBrains_Mono'] text-sm shrink-0 w-36">{m.mode}</span>
                  <span className="text-[#A1A1AA] text-sm">{m.use}</span>
                </div>
              ))}
            </div>
          </div>
          {/* White Balance */}
          <div>
            <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-2">{c.tech_wb_title}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {c.tech_wb.map((w, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                  <span className="text-white text-sm font-medium">{w.preset}</span>
                  <p className="text-[#A1A1AA] text-xs mt-0.5">{w.when}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* 5 ─ Post-Processing Basics */}
      <motion.div
        variants={item}
        className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
        data-testid="fundamentals-postprocessing"
      >
        <div className="flex items-center gap-3 mb-4">
          <Palette weight="fill" className="w-6 h-6 text-[#F5A524]" />
          <div>
            <h3 className="font-['Outfit'] text-lg text-white font-medium">{c.pp_title}</h3>
            <p className="text-[#71717A] text-sm">{c.pp_desc}</p>
          </div>
        </div>
        <div className="space-y-2">
          {c.pp_steps.map((s, i) => (
            <div key={i} className="flex items-start gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
              <div className="w-7 h-7 rounded-full bg-[#F5A524]/10 text-[#F5A524] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                {i + 1}
              </div>
              <div>
                <span className="text-white text-sm font-medium">{s.step}</span>
                <p className="text-[#A1A1AA] text-xs mt-0.5 leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
